import { useGetCompanyAllQuery } from 'services/user/queries';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  Icon,
  InputDateTime,
  InputSelect,
  InputText,
  TableListData,
} from 'components';
import { Button, InputWraper, useForm, IUseFormStructureItem } from 'modules';
import React, { useCallback, useMemo, useState } from 'react';
import {
  useGetRequestWithdrawExportQuery,
  useGetRequestWihtdraw,
} from 'services';
import {
  GetAllListWithdrawRequestEntity,
  GetOwnListWithdrawRequestEntity,
} from 'services/openapi';
import { exportHeaders, type FilterType, getInitFilterValues } from './config';
import { Link, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { convertSelectStatusOptions, convertVariables } from './config';
import { WithdrawRequestEntity } from 'services/openapi';
import ApproveRequestWithdraw from './approve';
import ApproveBidvAutoRequestWithdraw from './approveBidvAuto';
import RejectRequestWithdraw from './reject';
import { RequestWithdrawStatus } from 'configs/constants';
import { format } from 'date-fns';
import { NumberUtils } from 'modules';
import { RequestWithdrawStatusColor } from 'configs/constants';
import './CheckRequestWithdraw.scss';
import { DEFAULT_PAGE } from 'configs';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import {
  formatColumn,
  formatRowData,
  formatRowDataTotal,
} from 'configs/cell_xlsx';
import { removeVietnameseTones } from 'utils/helpers';
import { Actions } from 'configs/actions';
import { useManagementAccess } from 'utils/hooks/useManagementAccess';
import { MainRouteHelpers, MainRouteKeys } from 'routes/enums';

const getTransactionDetailPath = (transactionCode: string) =>
  MainRouteKeys.PointTransactionDetails.replace(
    ':transactionCode',
    encodeURIComponent(transactionCode),
  );

const formatApprovalMethod = (approvalMethod?: string) => {
  if (approvalMethod === 'MANUAL') return 'Duyệt (Chuyển tiền thủ công)';
  if (approvalMethod === 'AUTO_BIDV') {
    return 'Duyệt (Chuyển tiền tự động - BIDV)';
  }
  return approvalMethod || '';
};

const CheckRequestWithdrawList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);
  const { data: companyInfo } = useGetCompanyAllQuery();
  const companyOptions = [
    { label: 'Tất cả', value: '' },
    ...(companyInfo?.map((item) => ({
      label: item.compName,
      value: item.compId,
    })) || []),
  ];

  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);
  const [openPopUpAcceptRequest, setOpenPopUpAcceptRequest] = useState(false);
  const [openPopUpApproveBidvAuto, setOpenPopUpApproveBidvAuto] =
    useState(false);
  const [openPopUpRejectRequest, setOpenPopUpRejectRequest] = useState(false);
  const [itemId, setItemId] = useState<WithdrawRequestEntity | null>(null);
  const [selectedItems, setSelectedItems] = useState<WithdrawRequestEntity[]>(
    [],
  );
  const [approveBidvAutoTargetIds, setApproveBidvAutoTargetIds] = useState<
    string[]
  >([]);
  const [refetchDataCurrentPage, setRefetchDataCurrentPage] = useState(0);
  const [clearSelectedRowsKey, setClearSelectedRowsKey] = useState(0);

  const canPerformAction = useManagementAccess();
  const canView = canPerformAction(Actions.WithdrawPoint_ListRequest);
  const canApprove = canPerformAction(Actions.WithdrawPoint_ApproveRequest);
  const canReject = canPerformAction(Actions.WithdrawPoint_RejectRequest);
  const canApproveBidvAuto = canPerformAction(
    Actions.WithdrawPoint_ApproveBidvAutoRequest,
  );
  const selectedIds = useMemo(
    () => selectedItems.map((item) => item.id),
    [selectedItems],
  );

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        // refreshData: variables.refreshData + 1,
      });
      setSelectedItems([]);
      setClearSelectedRowsKey((prev) => prev + 1);
    },
    [variables],
  );

  const forceUpdateCurrentPage = useCallback(() => {
    setSelectedItems([]);
    setClearSelectedRowsKey((prev) => prev + 1);
    setRefetchDataCurrentPage((prev) => prev + 1);
  }, []);

  const handleSelectedRows = useCallback((data: any) => {
    setSelectedItems((prev) => {
      if (data.action === 'select') {
        if (prev.some((item) => item.id === data.row.id)) return prev;
        return [...prev, data.row];
      }

      return prev.filter((item) => item.id !== data.row.id);
    });
  }, []);

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'companyId',
      label: 'Doanh nghiệp/HTX',
      defaultValue: initFilterValues.companyId,
    },
    {
      name: 'phoneNumber',
      label: 'Tài khoản',
      defaultValue: initFilterValues.phoneNumber,
    },
    {
      name: 'transactionStatus',
      label: 'Trạng thái',
      defaultValue: initFilterValues.transactionStatus,
      validate: Yup.string().required('Vui lòng chọn Trạng thái'),
    },
    {
      name: 'fromTime',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: initFilterValues.fromTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.toTime).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.toTime);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'toTime',
      label: 'Ngày & giờ kết thúc',
      defaultValue: initFilterValues.toTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.fromTime).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.fromTime);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
  ];

  const columnsDefined: ColumnDef<WithdrawRequestEntity>[] = [
    {
      accessorKey: 'id',
      header: 'Mã Y/C Rút điểm',
      cell: (info) => (
        <Link
          to={MainRouteHelpers.checkRequestWithdrawDetail(info.row.original.id)}
          state={{ returnTo: `${location.pathname}${location.search}` }}
          title={`Xem chi tiết yêu cầu rút điểm ${info.row.original.id}`}
        >
          {info.row.original.id}
        </Link>
      ),
      meta: { columnSize: { minWidth: 180 } },
    },
    {
      accessorKey: 'transactions',
      header: 'Mã giao dịch',
      cell: (info) => {
        const transactions = info.row.original.transactions || [];
        if (!transactions.length) return <span>-</span>;

        return (
          <div className='withdraw-transaction-links'>
            {transactions.map((transaction) => (
              <Link
                key={transaction.id}
                to={getTransactionDetailPath(transaction.transactionCode)}
                target='_blank'
                rel='noreferrer'
                title={`${transaction.transactionType} - ${transaction.status}`}
              >
                {transaction.transactionCode}
              </Link>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Tài khoản',
    },
    {
      accessorKey: 'name',
      header: 'Ngân hàng',
      cell: (info) => {
        // return <div className='text-center'>{info.row.original?.bankAccount?.bank?.shortName} - {info.row.original?.bankAccount?.bank?.name}</div>
        return (
          <div className='text-center'>
            {info.row.original?.bankAccount?.bank?.shortName}
          </div>
        );
      },
    },
    {
      accessorKey: 'accountName',
      header: 'Tên tài khoản',
      cell: (info) => {
        return (
          <div className='text-center'>
            {info.row.original?.bankAccount?.accountName}
          </div>
        );
      },
    },
    {
      accessorKey: 'accountNumber',
      header: 'Số tài khoản',
      cell: (info) => {
        return (
          <div className='text-center'>
            {info.row.original?.bankAccount?.accountNumber}
          </div>
        );
      },
    },
    {
      accessorKey: 'amount',
      header: 'Số tiền',
      cell: (info) => {
        return (
          <div className='textDanger'>
            {info.getValue()
              ? NumberUtils.toFormatNumber(Number(info.getValue()))
              : '0'}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: (info) => (
        <div
          className={
            RequestWithdrawStatusColor[
              info.getValue() as keyof typeof RequestWithdrawStatusColor
            ]
          }
        >
          {String(info.getValue())}
        </div>
      ),
    },
    // {
    //   accessorKey: 'transactions',
    //   header: 'GD rút điểm',
    //   cell: (info) => {
    //     const txs: any  = info.getValue();
    //     console.log(txs)
    //     if(!txs || txs?.length == 0) return <></>
    //     const txReuest = txs.find((tx: any) => tx.transactionType === PointTransactionTypesColor['Rút điểm']);
    //     return <div className="textInfo">{txReuest.id}</div>
    //   }
    // },
    // {
    //   accessorKey: 'transactions',
    //   header: 'GD hoàn điểm',
    //   cell: (info) => {
    //     const txs: any  = info.getValue();
    //     if(!txs || txs?.length == 0) return <></>
    //     const txReuest = txs.find((tx: any) => tx.transactionType === PointTransactionTypesColor['Hoàn điểm']);
    //     return <div className="textDanger">{txReuest.id}</div>
    //   }
    // },
    {
      accessorKey: 'createdAt',
      header: 'Thời gian gửi',
      cell: (info) =>
        format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy'),
    },
    {
      accessorKey: 'approveMethod',
      header: 'Phương pháp duyệt',
      cell: (info) => (
        <div className='text-center'>
          {formatApprovalMethod(
            String(
              info.getValue() ||
                (
                  info.row.original as WithdrawRequestEntity & {
                    approvalMethod?: string;
                  }
                )?.approvalMethod ||
                '',
            ),
          )}
        </div>
      ),
    },
    {
      accessorKey: 'confirmAt',
      header: 'Thời gian duyệt',
      cell: (info) =>
        info.getValue()
          ? format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy')
          : '',
    },
    {
      accessorKey: 'rejectReason',
      header: 'Nguyên nhân từ chối',
    },
    {
      accessorKey: 'transactionImageUrl',
      header: 'Hình ảnh',
      cell: (info) => {
        // if (!info.row.original.transactionImageUrl) return <div className="action__upload">Upload</div>
        if (!info.row.original.transactionImageUrl) return <></>;
        return (
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => {
              window.open(info.row.original.transactionImageUrl, '_blank');
            }}
          >
            <img
              style={{ width: 'auto', height: 25, objectFit: 'contain' }}
              src={info.row.original.transactionImageUrl}
              alt='Hình ảnh'
            />
          </div>
        );
      },
    },
    {
      id: 'action',
      header: 'Thao tác',
      meta: {
        columnSize: {
          width: 450,
          minWidth: 450,
        },
      },
      cell: (info) => {
        const canProcess =
          info.row.original.status === RequestWithdrawStatus['Đang xử lý'] ||
          info.row.original.status ===
            RequestWithdrawStatus['Chuyển tiền tự động thất bại'];

        return (
          <div className='withdraw-actions'>
            <Link
              className='withdraw-actions__button withdraw-actions__button--detail'
              to={MainRouteHelpers.checkRequestWithdrawDetail(
                info.row.original.id,
              )}
              state={{ returnTo: `${location.pathname}${location.search}` }}
            >
              <Icon.ViewDetailIcon />
              Chi tiết
            </Link>

            {canProcess && canApprove && (
              <button
                type='button'
                className='withdraw-actions__button withdraw-actions__button--approve'
                onClick={() => {
                  setOpenPopUpAcceptRequest(true);
                  setItemId(info.row.original);
                }}
              >
                <Icon.Checked />
                Duyệt (Chuyển tiền Thủ công)
              </button>
            )}

            {canProcess && canApproveBidvAuto && (
              <button
                type='button'
                className='withdraw-actions__button withdraw-actions__button--bidv'
                onClick={() => {
                  setApproveBidvAutoTargetIds([info.row.original.id]);
                  setOpenPopUpApproveBidvAuto(true);
                  setItemId(info.row.original);
                }}
              >
                <Icon.BankBuilding />
                Duyệt (Chuyển tiền Tự động - BIDV)
              </button>
            )}

            {canProcess && canReject && (
              <button
                type='button'
                className='withdraw-actions__button withdraw-actions__button--reject'
                onClick={() => {
                  setOpenPopUpRejectRequest(true);
                  setItemId(info.row.original);
                }}
              >
                <Icon.Close />
                Từ chối
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const { isFetching: isLoadingExport, refetch } =
    useGetRequestWithdrawExportQuery({
      ...convertVariables(variables),
      page: '1',
      pageSize: String(
        (dataTable as unknown as GetAllListWithdrawRequestEntity)?.total ?? 0,
      ),
    });

  const handleExportExcel = async () => {
    // Add some data rows
    const allData = await refetch({
      ...convertVariables(variables),
      page: DEFAULT_PAGE,
      pageSize: String(
        (dataTable as unknown as GetAllListWithdrawRequestEntity)?.total,
      ),
    } as any);

    const dataArray = allData?.data?.data;

    const result = Array.isArray(dataArray) ? dataArray : [];

    //
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Columns
    const headerColumn = exportHeaders.map((header) => {
      return {
        header: header,
        key: header,
        width: 40,
      };
    });
    worksheet.columns = headerColumn;

    // Total
    const totalAmount = result
      .map((item: WithdrawRequestEntity) => item.amount)
      .reduce((acc, curr) => acc + curr, 0);

    const getValueSumAmount = NumberUtils.toFormatNumber(
      allData.data?.total as number,
    );
    const row = worksheet.addRow([
      '',
      '',
      'Tổng số tiền (Total)',
      totalAmount,
      '',
      'Tổng lệnh (Total order)',
      getValueSumAmount,
      '',
      '',
    ]);
    row.eachCell((cell, colNumber) => {
      if (
        colNumber === 3 ||
        colNumber === 4 ||
        colNumber === 6 ||
        colNumber === 7
      ) {
        formatRowDataTotal(cell);
      }
    });

    // Headers
    const firstRow = worksheet.getRow(1);
    firstRow.eachCell((cell) => formatColumn(cell));

    // Data
    result.forEach((item: WithdrawRequestEntity, index: number) => {
      const transactionCodes =
        item.transactions
          ?.map((transaction) => transaction.transactionCode)
          .join(', ') || '';
      const approvalMethod =
        (
          item as WithdrawRequestEntity & {
            approveMethod?: string;
            approvalMethod?: string;
          }
        ).approveMethod ||
        (item as WithdrawRequestEntity & { approvalMethod?: string })
          .approvalMethod ||
        '';
      const approvalMethodText = formatApprovalMethod(approvalMethod);

      const row = worksheet.addRow([
        index + 1,
        transactionCodes,
        item.phoneNumber,
        removeVietnameseTones(item.bankAccount.accountName),
        'Rut diem tu tai khoan ' + item.phoneNumber,
        item.amount,
        item.bankAccount.accountNumber,
        removeVietnameseTones(`${item.bankAccount.bank.code}`) +
          '-' +
          removeVietnameseTones(`${item.bankAccount.bank.name}`),
        item.bankAccount.bank.shortName || 'KHÔNG HỖ TRỢ',
        item.status,
        item.createdAt
          ? format(new Date(String(item.createdAt)), 'HH:mm:ss dd/MM/yyyy')
          : '',
        approvalMethodText,
        item.confirmAt
          ? format(new Date(String(item.confirmAt)), 'HH:mm:ss dd/MM/yyyy')
          : '',
        item.rejectReason || '',
        item.transactionImageUrl || '',
      ]);
      row.eachCell((cell) => formatRowData(cell));
    });

    // Generate Excel and trigger file download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const now = new Date();
    saveAs(
      blob,
      `Danh_sach_yeu_cau_rut_diem_${now.toLocaleTimeString('vn-VN', { hour12: false }).replace(/:/g, '_')}_${now.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '_')}.xlsx`,
    );
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('companyId')}
                renderInput={(props) => (
                  <InputSelect {...props} options={companyOptions} />
                )}
              />
            </div>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('phoneNumber')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionStatus')}
                isRequired
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectStatusOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>
          </div>
          {canView && (
            <div className='textCenter mt16 wrapButtonFilter'>
              <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
              <Button
                className='ml15'
                onClick={handleExportExcel}
                label={'Tải về DS'}
                type='submit'
                buttonType='info'
                isLoading={isLoadingExport}
              />
            </div>
          )}
        </form>
      </Card>

      <div className='check-request-withdraw-table'>
        <TableListData<GetOwnListWithdrawRequestEntity>
          columns={
            columnsDefined as ColumnDef<GetOwnListWithdrawRequestEntity>[]
          }
          useQuery={useGetRequestWihtdraw}
          variables={variables}
          setVariables={setVariables}
          convertVariables={convertVariables}
          refetchDataCurrentPage={refetchDataCurrentPage}
          clearSelectedRowsKey={clearSelectedRowsKey}
          orderColumn
          callBackData={setDataTable}
          selectable={canApproveBidvAuto}
          canSelectable={(item) =>
            (item as unknown as WithdrawRequestEntity)?.status ===
            RequestWithdrawStatus['Đang xử lý']
          }
          onSelectedRowsCallback={handleSelectedRows}
          customSelectedFooter={() => (
            <div style={{ display: 'inline-flex', gap: 8, marginLeft: 12 }}>
              {canApproveBidvAuto && selectedIds.length > 0 && (
                <Button
                  label={`Duyệt (Chuyển tiền Tự động - BIDV) đã chọn (${selectedIds.length})`}
                  buttonType='success'
                  onClick={() => {
                    setApproveBidvAutoTargetIds(selectedIds);
                    setOpenPopUpApproveBidvAuto(true);
                  }}
                />
              )}
            </div>
          )}
        />
      </div>

      {canApprove && openPopUpAcceptRequest && (
        <ApproveRequestWithdraw
          data={itemId}
          onClose={() => setOpenPopUpAcceptRequest(false)}
          forceUpdateTable={forceUpdateCurrentPage}
        ></ApproveRequestWithdraw>
      )}
      {canApproveBidvAuto && openPopUpApproveBidvAuto && (
        <ApproveBidvAutoRequestWithdraw
          ids={approveBidvAutoTargetIds}
          onClose={() => {
            setOpenPopUpApproveBidvAuto(false);
            setApproveBidvAutoTargetIds([]);
            setItemId(null);
          }}
          forceUpdateTable={forceUpdateCurrentPage}
        />
      )}
      {canReject && openPopUpRejectRequest && (
        <RejectRequestWithdraw
          data={itemId}
          onClose={() => setOpenPopUpRejectRequest(false)}
          forceUpdateTable={forceUpdateCurrentPage}
        ></RejectRequestWithdraw>
      )}
    </>
  );
};

export default CheckRequestWithdrawList;
